import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { fmtDate } from "../lib/format";
import InfoTip from "../components/InfoTip";
import PasswordDialog from "../components/PasswordDialog";
import { toast } from "sonner";
import {
  Plus, KeyRound, Ban, RotateCcw, Pencil, X, Eye, EyeOff, Copy,
} from "lucide-react";

// Admin-only user management. Passwords are generated server-side and shown
// exactly once — only the bcrypt hash is stored.

const errDetail = (e, fallback) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  return fallback;
};

// "just now" / "3h ago" / "2 days ago" / "14 Jun 2026"
const relTime = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 14) return `${days} day${days === 1 ? "" : "s"} ago`;
  return fmtDate(iso);
};

export default function Users() {
  const { rawData, viewAs } = useData();
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);   // user being edited
  const [secret, setSecret] = useState(null);       // { email, password, title }
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightEmail = (searchParams.get("highlight") || "").toLowerCase();
  const highlightRef = useRef(null);
  const [flashing, setFlashing] = useState(null);

  const principals = rawData?.principals || [];

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(errDetail(e, "Could not load users"));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Deep-link support: /admin/users?highlight=<email> — scroll to row + flash it
  // (used by the Personnel "Reset password" shortcut).
  useEffect(() => {
    if (!highlightEmail || !users) return;
    if (!users.some((u) => u.email.toLowerCase() === highlightEmail)) return;
    const el = highlightRef.current;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    setFlashing(highlightEmail);
    const t = setTimeout(() => {
      setFlashing(null);
      const next = new URLSearchParams(searchParams);
      next.delete("highlight");
      setSearchParams(next, { replace: true });
    }, 2400);
    return () => clearTimeout(t);
  }, [users, highlightEmail, searchParams, setSearchParams]);

  const principalName = (pid) =>
    principals.find((p) => p.id === pid)?.name || pid || "—";

  const resetPassword = async (u) => {
    if (!window.confirm(`Generate a new password for ${u.name || u.email}? Their current password stops working immediately.`)) return;
    try {
      const { data } = await api.post(`/users/${encodeURIComponent(u.email)}/reset-password`);
      setSecret({ email: u.email, password: data.password, title: "New password" });
      load();
    } catch (e) {
      toast.error(errDetail(e, "Could not reset password"));
    }
  };

  const setStatus = async (u, action) => {
    try {
      await api.post(`/users/${encodeURIComponent(u.email)}/${action}`);
      toast.success(action === "disable" ? `${u.email} disabled` : `${u.email} re-enabled`);
      load();
    } catch (e) {
      toast.error(errDetail(e, "Could not update account"));
    }
  };

  return (
    <div className="space-y-8" data-testid="users-view">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="eyebrow">Access</div>
          <h2 className="section-h mt-1">User management · {users?.length ?? "…"}</h2>
          <div className="text-xs font-mono-num text-[var(--soft-fg)] mt-1">
            Passwords are generated automatically and shown once — copy them before closing.
          </div>
        </div>
        <button
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-2 px-3 py-2 bg-[var(--ink)] text-white text-xs font-display tracked-tight uppercase"
          data-testid="add-user-btn"
        >
          <Plus size={12} /> Add user
        </button>
      </div>

      {viewAs && (
        <div className="p-3 border border-[#B45309] bg-[#FDF6EF] text-xs font-mono-num text-[#7C3A06]" data-testid="users-viewas-hint">
          User management is a firm-wide action — unavailable while acting as a principal. Switch View as → Firm-wide.
        </div>
      )}

      <div className="border border-[var(--rule)] bg-white overflow-x-auto thin-scroll">
        <table className="w-full text-sm">
          <thead>
            <tr className="eyebrow text-left">
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">
                <span className="inline-flex items-center">
                  Linked principal
                  <InfoTip side="bottom" label="Linked principal">
                    Which portfolio this account is scoped to. Principals only ever see their linked portfolio; admins see everything regardless.
                  </InfoTip>
                </span>
              </th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Last login</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(users || []).map((u) => {
              const isMe = u.email === me?.email;
              const disabled = u.status === "disabled";
              const isHighlighted = flashing && u.email.toLowerCase() === flashing;
              return (
                <tr key={u.email}
                    ref={isHighlighted ? highlightRef : undefined}
                    className={`border-t border-[var(--rule)] transition-colors duration-500 ${disabled ? "opacity-50" : ""} ${isHighlighted ? "bg-[#FBF3D7]" : ""}`}
                    data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3">
                    <div className="text-[var(--ink)]">{u.name || "—"}{isMe && <span className="eyebrow ml-2 text-[var(--soft-fg)]">(you)</span>}</div>
                    <div className="font-mono-num text-[11px] text-[var(--soft-fg)]">{u.email}</div>
                    {u.role === "associate" && u.hasAssignedPassword && (
                      <AssignedPasswordReveal email={u.email} />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`chip ${u.role === "admin" ? "chip-yes" : "chip-tentative"}`} data-testid={`user-role-${u.email}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono-num text-[var(--muted-fg)]">
                    {principalName(u.principalId)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`chip ${disabled ? "chip-no" : "chip-yes"}`} data-testid={`user-status-${u.email}`}>
                      {disabled ? "Disabled" : "Active"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[11px] font-mono-num" data-testid={`user-lastlogin-${u.email}`}>
                    {u.lastLoginAt
                      ? <span className="text-[var(--muted-fg)]">{relTime(u.lastLoginAt)}</span>
                      : <span className="text-[var(--soft-fg)]">never</span>}
                  </td>
                  <td className="px-4 py-3 text-[11px] font-mono-num text-[var(--soft-fg)]">
                    {fmtDate(u.createdAt)}
                    {u.createdBy && u.createdBy !== "system_seed" && <div>by {u.createdBy.split("@")[0]}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <IconBtn title="Edit role & link" onClick={() => setEditUser(u)} testid={`edit-user-${u.email}`}>
                        <Pencil size={13} />
                      </IconBtn>
                      <IconBtn title="Reset password" onClick={() => resetPassword(u)} testid={`reset-password-${u.email}`}>
                        <KeyRound size={13} />
                      </IconBtn>
                      {disabled ? (
                        <IconBtn title="Re-enable account" onClick={() => setStatus(u, "enable")} testid={`enable-${u.email}`}>
                          <RotateCcw size={13} />
                        </IconBtn>
                      ) : (
                        <IconBtn title="Disable account" disabled={isMe} onClick={() => setStatus(u, "disable")} testid={`disable-${u.email}`}>
                          <Ban size={13} />
                        </IconBtn>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {users && users.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center eyebrow text-[var(--soft-fg)]">No users.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {addOpen && (
        <UserDialog
          title="Add user"
          principals={principals}
          personnel={rawData?.personnel || []}
          projects={rawData?.projects || []}
          onClose={() => setAddOpen(false)}
          onSubmit={async (form) => {
            const { data } = await api.post("/users", form);
            setAddOpen(false);
            setSecret({ email: data.user.email, password: data.password, title: "Account created" });
            load();
          }}
        />
      )}

      {editUser && (
        <UserDialog
          title="Edit user"
          principals={principals}
          personnel={rawData?.personnel || []}
          projects={rawData?.projects || []}
          initial={editUser}
          isSelf={editUser.email === me?.email}
          onClose={() => setEditUser(null)}
          onSubmit={async (form) => {
            await api.put(`/users/${encodeURIComponent(editUser.email)}`, {
              name: form.name, role: form.role, principalId: form.principalId ?? "",
              personnelId: form.personnelId ?? "",
            });
            setEditUser(null);
            toast.success("User updated");
            load();
          }}
        />
      )}

      {secret && <PasswordDialog secret={secret} onClose={() => setSecret(null)} />}
    </div>
  );
}

function IconBtn({ title, onClick, disabled, testid, children }) {
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      className="p-2 border border-[var(--rule)] hover:bg-[var(--paper-soft)] hover:border-[var(--rule-strong)] disabled:opacity-30 disabled:pointer-events-none"
      data-testid={testid}
    >
      {children}
    </button>
  );
}

// Click-to-reveal admin-only password chip. Only rendered for ASSOCIATE rows
// that have a stored assigned_password. Server-side the endpoint 403s every
// non-admin (require_admin) and 404s for non-associate accounts. UI never
// prefetches — the plaintext only crosses the wire on explicit click.
function AssignedPasswordReveal({ email }) {
  const [pw, setPw] = useState(null);
  const [loading, setLoading] = useState(false);
  const reveal = async () => {
    if (pw) { setPw(null); return; }  // toggle off
    setLoading(true);
    try {
      const { data } = await api.get(`/users/${encodeURIComponent(email)}/assigned-password`);
      setPw(data.password);
    } catch (e) {
      const msg = typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not fetch assigned password";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pw);
      toast.success("Password copied");
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="mt-1.5" data-testid={`assigned-password-block-${email}`}>
      <div className="inline-flex items-center gap-1.5 flex-wrap">
        <button
          type="button"
          onClick={reveal}
          disabled={loading}
          className="inline-flex items-center gap-1 px-2 py-0.5 border border-[var(--rule)] hover:border-[var(--rule-strong)] hover:bg-[var(--paper-soft)] text-[10px] font-display tracked-tight uppercase text-[var(--muted-fg)] disabled:opacity-50"
          data-testid={`reveal-assigned-password-${email}`}
          title={pw ? "Hide password" : "Reveal assigned password"}
        >
          {pw ? <EyeOff size={10} /> : <Eye size={10} />}
          Assigned password
        </button>
        {pw && (
          <>
            <code
              className="px-1.5 py-0.5 bg-[var(--paper-soft)] border border-[var(--rule)] font-mono-num text-[11px] text-[var(--ink)] select-all"
              data-testid={`assigned-password-value-${email}`}
            >{pw}</code>
            <button
              type="button"
              onClick={copy}
              className="p-1 border border-[var(--rule)] hover:bg-[var(--paper-soft)]"
              data-testid={`copy-assigned-password-${email}`}
              title="Copy to clipboard"
            >
              <Copy size={10} />
            </button>
          </>
        )}
      </div>
      {pw && (
        <div className="eyebrow mt-1 text-[var(--soft-fg)] normal-case tracking-[0.06em]" data-testid={`assigned-password-help-${email}`}>
          Assigned by admin — visible to administrators.
        </div>
      )}
    </div>
  );
}

function UserDialog({ title, principals, personnel, projects, initial, isSelf, onClose, onSubmit }) {
  const [form, setForm] = useState(() => ({
    email: initial?.email || "",
    name: initial?.name || "",
    role: initial?.role || "principal",
    principalId: initial?.principalId || "",
    personnelId: initial?.personnelId || "",
  }));
  // Associates never link to a principal — projects grant them access on
  // a per-project basis via the Editors list on the project form.
  const isAssociate = form.role === "associate";
  const grantedProjects = (() => {
    if (!isAssociate || !initial?.email) return [];
    const pid = form.personnelId || initial?.personnelId;
    return (projects || []).filter(
      (p) => p.createdBy === initial.email || (pid && (p.editors || []).includes(pid))
    );
  })();
  const [busy, setBusy] = useState(false);
  const editing = !!initial;

  const submit = async () => {
    if (!editing && !form.email.trim()) { toast.error("Email required"); return; }
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      await onSubmit({ ...form, email: form.email.trim().toLowerCase() });
    } catch (e) {
      toast.error(errDetail(e, "Could not save user"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} testid="user-dialog">
      <div className="eyebrow">{editing ? "Edit" : "New account"}</div>
      <h3 className="section-h mt-1 mb-5">{title}</h3>
      <div className="space-y-4">
        <Field label="Email">
          <input
            type="email" value={form.email} disabled={editing}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="w-full border border-[var(--rule)] p-2 text-sm font-mono-num disabled:bg-[var(--paper-soft)] disabled:text-[var(--soft-fg)]"
            placeholder="first.last@monkmackenzie.com"
            data-testid="user-form-email"
          />
        </Field>
        <Field label="Full name">
          <input
            type="text" value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full border border-[var(--rule)] p-2 text-sm"
            data-testid="user-form-name"
          />
        </Field>
        <Field label="Role">
          <select
            value={form.role} disabled={isSelf}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            className="w-full border border-[var(--rule)] p-2 text-sm disabled:bg-[var(--paper-soft)]"
            data-testid="user-form-role"
          >
            <option value="admin">Admin — full firm-wide access</option>
            <option value="principal">Principal — own portfolio only</option>
            <option value="associate">Associate — invited-project access only</option>
            <option value="leave_editor">Leave editor — annual leave page only</option>
          </select>
          {isSelf && <div className="eyebrow mt-1 text-[var(--soft-fg)] normal-case tracking-[0.06em]">You cannot change your own role.</div>}
        </Field>
        {!isAssociate ? (
          <Field label="Linked principal (portfolio)">
            <select
              value={form.principalId}
              onChange={(e) => setForm((f) => ({ ...f, principalId: e.target.value }))}
              className="w-full border border-[var(--rule)] p-2 text-sm font-mono-num"
              data-testid="user-form-principal"
            >
              <option value="">— none —</option>
              {principals.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
        ) : null}
        {form.role === "associate" && (
          <Field label="Linked personnel record">
            <select
              value={form.personnelId}
              onChange={(e) => setForm((f) => ({ ...f, personnelId: e.target.value }))}
              className="w-full border border-[var(--rule)] p-2 text-sm font-mono-num"
              data-testid="user-form-personnel"
            >
              <option value="">— none —</option>
              {(personnel || []).map((p) => <option key={p.id} value={p.id}>{p.fullName} · {p.tier}</option>)}
            </select>
            <div className="eyebrow mt-1 text-[var(--soft-fg)] normal-case tracking-[0.06em]">
              Associates only see projects where this personnel record appears in the project's Editors list.
            </div>
          </Field>
        )}
        {isAssociate && initial?.email && (
          <div className="mt-2">
            <div className="eyebrow mb-1.5">Projects with access</div>
            {grantedProjects.length === 0 ? (
              <div className="text-xs text-[var(--soft-fg)] font-mono-num" data-testid="assoc-projects-empty">
                No projects yet. Grant access from the Editors list on the project form.
              </div>
            ) : (
              <ul className="space-y-1" data-testid="assoc-projects-list">
                {grantedProjects.map((p) => (
                  <li key={p.id} className="text-xs font-mono-num flex items-center gap-2">
                    <span className="inline-block w-1 h-1 rounded-full bg-[var(--muted-fg)]" />
                    <Link
                      to={`/admin/my-projects/edit/${p.id}`}
                      className="hover:underline"
                      onClick={onClose}
                      data-testid={`assoc-project-${p.id}`}
                    >{p.id} · {p.name}</Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {!editing && (
          <div className="p-3 bg-[var(--paper-soft)] text-xs font-mono-num text-[var(--muted-fg)]">
            A strong password is generated automatically and shown once after creation.
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button onClick={onClose} className="px-4 py-2 border border-[var(--rule-strong)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]" data-testid="user-form-cancel">
          Cancel
        </button>
        <button onClick={submit} disabled={busy} className="px-4 py-2 bg-[var(--ink)] text-white text-xs font-display tracked-tight uppercase disabled:opacity-50" data-testid="user-form-submit">
          {editing ? "Save" : "Create user"}
        </button>
      </div>
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Modal({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white border border-[var(--rule-strong)] w-full max-w-md p-6 animate-fade-up" data-testid={testid}>
        <button onClick={onClose} className="absolute top-3 right-3 p-1 hover:bg-[var(--paper-soft)]" aria-label="close">
          <X size={14} />
        </button>
        {children}
      </div>
    </div>
  );
}
